import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAssetPerformance, useAssetHistory, useAddTransaction } from '../../hooks/useAssets';
import { PageLoader } from '../../components/common/LoadingSpinner';
import {
  hideCurrency,
  hidePercent,
  formatDate,
  gainLossClass,
} from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';
import { ASSET_TYPE_LABELS, ASSET_TYPE_BG, type AssetType, type TransactionForm, type Transaction } from '../../types';

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showTxModal, setShowTxModal] = useState(false);
  const { data: performance, isLoading } = useAssetPerformance(id!);
  const { data: history } = useAssetHistory(id!);
  const { isVisible } = useValuesVisibility();

  if (isLoading) return <PageLoader />;
  if (!performance) return null;

  const { asset, performance: perf } = performance;

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/assets')}
        className="btn-ghost -ml-2"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Voltar para Ativos
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:justify-between">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-white font-mono">{asset.code}</h1>
            <span
              className={`badge ${ASSET_TYPE_BG[asset.type as AssetType] || 'badge-blue'}`}
            >
              {ASSET_TYPE_LABELS[asset.type as AssetType] || asset.type}
            </span>
          </div>
          <p className="text-white/50 mt-1">{asset.name}</p>
          {asset.sector && (
            <p className="text-xs text-white/30 mt-0.5">Setor: {asset.sector}</p>
          )}
        </div>
        <button
          onClick={() => setShowTxModal(true)}
          className="btn-primary self-start"
        >
          <PlusIcon className="h-4 w-4" />
          Nova Transação
        </button>
      </div>

      {/* Performance Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="stat-card">
          <span className="stat-label">Quantidade</span>
          <span className="stat-value text-white font-mono">{perf.quantity}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Preço Médio</span>
          <span className="stat-value text-white">{hideCurrency(perf.averagePrice, isVisible)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Preço Atual</span>
          <span className="stat-value text-white">
            {perf.currentPrice ? hideCurrency(perf.currentPrice, isVisible) : '—'}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Total Investido</span>
          <span className="stat-value text-white">{hideCurrency(perf.totalInvested, isVisible)}</span>
        </div>
        <div className="stat-card col-span-2">
          <span className="stat-label">Valor Atual</span>
          <span className="stat-value text-white">{hideCurrency(perf.currentValue, isVisible)}</span>
        </div>
        <div className="stat-card col-span-2">
          <span className="stat-label">Lucro / Prejuízo</span>
          <span className={`stat-value ${gainLossClass(perf.gainLoss)}`}>
            {hideCurrency(perf.gainLoss, isVisible)}
          </span>
          <span className={`text-sm ${gainLossClass(perf.gainLossPercent)}`}>
            {hidePercent(perf.gainLossPercent, isVisible)}
          </span>
        </div>
      </div>

      {/* Transaction History */}
      <div className="card">
        <h2 className="text-sm font-semibold text-white mb-4">Histórico de Transações</h2>

        {history && history.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th className="text-right">Qtd.</th>
                  <th className="text-right">Preço</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Taxas</th>
                  <th>Corretora</th>
                </tr>
              </thead>
              <tbody>
                {history.map((tx: Transaction) => (
                  <tr key={tx.id}>
                    <td>{formatDate(tx.transactionDate)}</td>
                    <td>
                      <span
                        className={
                          (tx.type === 'BUY' ? 'badge-green' : 'badge-red') + ' badge'
                        }
                      >
                        {tx.type === 'BUY' ? 'Compra' : 'Venda'}
                      </span>
                    </td>
                    <td className="text-right font-mono">{tx.quantity}</td>
                    <td className="text-right font-mono">{hideCurrency(tx.price, isVisible)}</td>
                    <td className="text-right font-mono">{hideCurrency(tx.totalValue, isVisible)}</td>
                    <td className="text-right font-mono text-white/40">
                      {tx.fees ? hideCurrency(tx.fees, isVisible) : '—'}
                    </td>
                    <td className="text-white/50">{tx.broker || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-white/30 text-sm text-center py-8">
            Nenhuma transação registrada
          </p>
        )}
      </div>

      {showTxModal && (
        <AddTransactionModal
          assetId={id!}
          assetCode={asset.code}
          onClose={() => setShowTxModal(false)}
        />
      )}
    </div>
  );
}

function AddTransactionModal({
  assetId,
  assetCode,
  onClose,
}: {
  assetId: string;
  assetCode: string;
  onClose: () => void;
}) {
  const { mutate: addTransaction, isPending } = useAddTransaction(assetId);
  const [form, setForm] = useState({
    type: 'BUY',
    quantity: '',
    price: '',
    fees: '',
    transactionDate: new Date().toISOString().split('T')[0],
    broker: '',
    notes: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addTransaction(
      {
        type: form.type as 'BUY' | 'SELL',
        quantity: Number(form.quantity),
        price: Number(form.price),
        fees: form.fees ? Number(form.fees) : 0,
        transactionDate: form.transactionDate,
        broker: form.broker || undefined,
        notes: form.notes || undefined,
      } as TransactionForm,
      { onSuccess: () => onClose() },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-800 rounded-2xl border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h2 className="text-lg font-semibold text-white">
            Nova Transação — {assetCode}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label">Tipo</label>
            <div className="flex rounded-lg bg-surface-900 p-1">
              <button
                type="button"
                onClick={() => setForm({ ...form, type: 'BUY' })}
                className={`flex-1 py-2 text-sm rounded-md transition-colors ${
                  form.type === 'BUY' ? 'bg-success-600 text-white' : 'text-white/50'
                }`}
              >
                Compra
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, type: 'SELL' })}
                className={`flex-1 py-2 text-sm rounded-md transition-colors ${
                  form.type === 'SELL' ? 'bg-danger-600 text-white' : 'text-white/50'
                }`}
              >
                Venda
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Quantidade</label>
              <input
                type="number"
                step="any"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="input"
                required
              />
            </div>
            <div>
              <label className="label">Preço</label>
              <input
                type="number"
                step="any"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="input"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Taxas</label>
              <input
                type="number"
                step="any"
                value={form.fees}
                onChange={(e) => setForm({ ...form, fees: e.target.value })}
                className="input"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="label">Data</label>
              <input
                type="date"
                value={form.transactionDate}
                onChange={(e) => setForm({ ...form, transactionDate: e.target.value })}
                className="input"
                required
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button type="submit" disabled={isPending} className="btn-primary flex-1">
              Registrar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
